import videojs from "video.js";
import { checkLive } from "./utils.js";
import getCameraIcon from "../templates/camera.svg.html";

const dom = videojs.dom;

videojs.registerComponent('MulticamCamera', class extends videojs.getComponent('Component') {
	constructor(player, options) {
		super(player, options);

		this.monitorTimeoutId = null;
		this.skipsInfo = [];
		this.isVisible = false;
		this.isThumbnailLoaded = true;
		this.currentThumbnailName = this.checkFinished() ? 'event' : 'camera';
		this.startAt = Number(this.options_.broadcast.startAt);
		this.finishAt = Number(this.options_.broadcast.finishAt);
		this.inputEl = this.$('input');
		this.thumbnailWrapperEl = this.$('.vjs-multicam__thumbnail-wrapper');
		this.stubEl = this.$('.vjs-multicam__stub');
		this.stubThumbnailEl = this.$('.vjs-multicam__stub-thumbnail');
		this.stubTimer = this.$('.vjs-multicam__stub-timer');
		this.thumbnailEl = this.$('.vjs-multicam__thumbnail');

		if (this.options_.broadcast.defaultSelected) {
			this.inputEl.checked = true;
		}

		this.setListeners();
		this.runMonitorState();
		this.getSkipsInfo();
	}

	async getSkipsInfo() {
		function getTimestampFromUrl(chunkUrl) {
			return chunkUrl
				.match(/(\d+)\.ts\/*(\d+)*\/*(\d+)*/) // достаёт 1693325749863.ts/1187/0 где часть после .ts не обязательна
				.slice(1, 4)
				.map((item) => item ? Number(item) : 0)
				.reduce((acc, item) => acc + item);
		}

		const masterPlaylistResponse = await fetch(this.options_.broadcast.playlistUrl);
		const masterPlaylistText = await masterPlaylistResponse.text();

		const firstMediaUrlMatch = /\n(?<firstMediaUrl>https?[^\s]*)/.exec(masterPlaylistText);
		if (!firstMediaUrlMatch) {
			return;
		}
		const { firstMediaUrl } = firstMediaUrlMatch.groups;
		if (!firstMediaUrl) {
			return;
		}

		const mediaPlaylistResponse = await fetch(firstMediaUrl);
		const mediaPlaylistText = await mediaPlaylistResponse.text();

		if (!mediaPlaylistText.includes('#EXT-X-DISCONTINUITY')) {
			return;
		}

		const playlistStartTimestamp = getTimestampFromUrl(mediaPlaylistText);

		const matchesWithDiscontinuity = (function matchAll(regex, string) {
			let matchItem;
			const matchList = [];
			while((matchItem = regex.exec(string)) !== null) {
				matchList.push(matchItem);
			}
			return matchList;
		})(
			new RegExp(
				`(?<urlBeforeSkip>https?[^\\s]+)\\n` +
				`#EXT-X-DISCONTINUITY\\n` +
				`#EXT-X-PROGRAM-DATE-TIME:.*\\n` +
				`#EXTINF:(?<chunkDuration>\\d+(\\.\\d+)?),\\n` +
				`(?<urlAfterSkip>https?[^\\s]+)`,
				'g'
			),
			mediaPlaylistText
		);

		const skipsInfo = matchesWithDiscontinuity.map((match) => {
			const { urlBeforeSkip, urlAfterSkip, chunkDuration } = match.groups;
			const [ startChunkTime, finish ] = [ urlBeforeSkip, urlAfterSkip ]
				.map((url) => getTimestampFromUrl(url) - playlistStartTimestamp);
			const chunkDurationInMs = chunkDuration * 1000;
			const start = startChunkTime + chunkDurationInMs;
			return { start, finish };
		});

		this.skipsInfo = skipsInfo;
	}

	setListeners() {
		this.on('MulticamCamera/changeData', () => {
			if (!this.checkStarted() || this.checkFinished()) return;
			this.thumbnailEl.src = this.getThumbnailUrl('camera');
		});

		this.stubThumbnailEl
			.addEventListener(
				'load',
				() => dom.removeClass(this.stubThumbnailEl, 'vjs-hidden')
			);

		this.stubThumbnailEl
			.addEventListener(
				'error',
				() => dom.addClass(this.stubThumbnailEl, 'vjs-hidden')
			);

		this.thumbnailEl
			.addEventListener(
				'load',
				() => {
					this.thumbnailWrapperEl.classList.remove('vjs-hidden');
					this.stubEl.classList.add('vjs-hidden');
					this.isThumbnailLoaded = true;
				}
			);

		this.thumbnailEl
			.addEventListener(
				'error',
				() => {
					const eventThumbnailUrl = this.getThumbnailUrl('event');
					this.isThumbnailLoaded = false;
					if (eventThumbnailUrl && eventThumbnailUrl !== this.thumbnailEl.src) {
						this.thumbnailEl.src = eventThumbnailUrl;
					} else {
						this.thumbnailWrapperEl.classList.add('vjs-hidden');
						this.stubEl.classList.remove('vjs-hidden');
					}
				}
			);

		this.on('click', async () => {
			if (this.checkSelected() || this.inputEl.disabled) return;

			this.player()
				.multicam()
				.setState({
					activeBroadcast: this.options_.broadcast
				});

			const currentTime = this.player().currentTime();
			const currentType = this.player().currentType();
			const volume = this.player().volume();
			const paused = this.player().paused();
			const checkSeekToCurrentTime = () => !checkLive(this.player()) && currentTime <= this.player().duration();
			const isCurrentLive = checkLive(this.player());
			const LiveDisplay = this.player().getDescendant(['controlBar', 'LiveDisplay']);
			const isBehindLive = LiveDisplay && LiveDisplay.checkBehindLive();
			const currentCamera = this.player()
				.multicam()
				.getCameras()
				.find((camera) => camera.checkSelected());

			const startDiff = currentCamera.startAt - this.startAt;
			const currentTimeWithSkips = currentCamera.skipsInfo.reduce((timestump, { start, finish }) => {
				if (timestump > start) {
					timestump += finish - start;
				}
				return timestump;
			}, currentTime * 1000); // => ms

			const adjustedCurrentTime = (
				(timestamp, skipsInfo) => {
					if (timestamp <= 0) {
						return 0;
					}
					const sumOfSkipsBehindTimestamp = skipsInfo
						.map(function msToSec({ start, finish }) {
							return {
								start: start / 1000,
								finish: finish / 1000
							};
						})
						.filter(function checkSkipBehindTimestamp({ start }) {
							return timestamp > start;
						})
						.reduceRight(function getSumOfSkips(acc, { start, finish }, index, arr) {
							const isFirstIteration = arr.length - 1 === index;
							if (start <= timestamp && timestamp < finish && isFirstIteration) {
								acc += timestamp - start;
							} else {
								acc += finish - start;
							}
							return acc;
						}, 0);

					return timestamp - sumOfSkipsBehindTimestamp;
				}
			)(
				currentTimeWithSkips / 1000 + startDiff,
				this.skipsInfo
			);

			const checkDefaultStart = () => isCurrentLive && !isBehindLive;

			// Нужно для плагина qualitySwitcher.
			this.player().multicam().setState({
				playerDataBeforeChangeSrc: {
					currentTime: () => !checkDefaultStart()
						? this.getPlaybackTime(adjustedCurrentTime)
						: 0,
					currentType,
					volume,
					paused
				}
			});

			const audioTracksOptions = this.prepareAudioTracksOptions();
			this.player().audiotracks().dispose();
			this.player().audiotracks(audioTracksOptions);

			this.player()
				.src({
					src: this.options_.broadcast.playlistUrl,
					type: currentType
				});

			// Нужно чтобы плагин qualitySwitcher сам восстанавливал состояние плеера после смены src.
			const isRestorePlaying = this.player().usingPlugin('qualitySwitcher') === false || (
				this.player().usingPlugin('qualitySwitcher')
					&& this.player().quality_handler
					&& this.player().quality_handler.supported !== 'native'
			);
			if (isRestorePlaying) {
				this.player()
					.one('canplay', () => {
						if (!checkDefaultStart() && adjustedCurrentTime !== 0) {
							this.player().currentTime(
								this.getPlaybackTime(adjustedCurrentTime)
							);
						}

						if (paused) {
							this.player().pause();
						}
					});
				this.player().load();
				this.player().play();
				this.player().volume(volume);
			}

			this.options_.handleClick();

			this.player()
				.trigger({
					type: 'MulticamCamera/select',
					isMainCamera: this.checkMain(),
					newCameraName: this.options_.broadcast.name
				});
		});
	}

	setVisibility(isVisible) {
		this.isVisible = isVisible;
		this.updateAvailability();
	}

	runMonitorState() {
		const monitor = () => {
			this.monitorTimeoutId = setTimeout(monitor, 1000);
			this.updateAvailability();
			this.updateThumbnailAfterBroadcastEnd();
			this.handleNotStartedBroadcast();
			this.updateRemainingTime();
		}
		monitor();
	}

	updateRemainingTime() {
		if (this.startAt * 1000 <= Date.now()) {
			this.stubTimer.innerText = '';
			return;
		}
		const remainingTime = this.getRemainingTime(this.startAt);
		this.stubTimer.innerText = remainingTime;
		this.trigger({
			type: 'MulticamCamera/updateRemainingTime',
			data: remainingTime
		});
	}

	handleNotStartedBroadcast() {
		if (!this.checkSelected() || this.checkStarted()) return;
		this.options_.handleNotStartedBroadcast();
	}

	updateThumbnailAfterBroadcastEnd() {
		if (!this.isVisible || !this.checkFinished() || this.currentThumbnailName === 'event') return;
		this.thumbnailEl.src = this.getThumbnailUrl('event');
	}

	createEl() {
		const el = dom.createEl(
			'label',
			{
				className: 'vjs-multicam__label'
			}
		);
		const input = dom.createEl(
			'input',
			{
				className: 'vjs-multicam__input'
			}, {
				type: 'radio',
				name: 'multicam-item'
			}
		);
		const container = dom.createEl(
			'div',
			{
				className: 'vjs-multicam__camera'
			}
		);
		const thumbnailWrapperEl = dom.createEl(
			'div',
			{
				className: 'vjs-multicam__thumbnail-wrapper'
			}
		);
		const thumbnailEl = dom.createEl(
			'img',
			{
				className: 'vjs-multicam__thumbnail'
			}, {
				src: this.checkFinished() ? this.getThumbnailUrl('event') : this.getThumbnailUrl('camera')
			}
		);
		const stubThumbnailEl = dom.createEl(
			'img',
			{
				className: 'vjs-multicam__stub-thumbnail'
			}, {
				src: this.getThumbnailUrl('event', false)
			}
		);
		const stubTimer = dom.createEl(
			'div',
			{
				className: 'vjs-multicam__stub-timer',
				innerText: this.getRemainingTime(this.options_.broadcast.startAt)
			}
		);
		const stubEl = dom.createEl(
			'div',
			{
				className: 'vjs-multicam__stub vjs-hidden'
			},
			{},
			[stubThumbnailEl, stubTimer]
		);
		const title = dom.createEl(
			'div',
			{
				className: 'vjs-multicam__title',
			},
			{},
			[
				dom.createEl(
					'span',
					{
						className: 'vjs-multicam__name',
						innerHTML: `${getCameraIcon({ width: 16, height: 16, type: 'enabled' })} ${this.options_.broadcast.name}`
					}
				)
			]
		);

		thumbnailWrapperEl.append(thumbnailEl);
		container.append(thumbnailWrapperEl, stubEl, title);
		el.append(input, container);
		return el;
	}

	updateCameraThumbnail(randomValue) {
		if (!this.checkStarted() || this.currentThumbnailName !== 'camera') return;

		const thumbnailUrl = new URL(this.thumbnailEl.src);
		thumbnailUrl.searchParams.set('rnd', randomValue);

		const tempImage = new Image();
		tempImage.addEventListener(
			'load',
			() => {
				this.thumbnailEl.src = thumbnailUrl.toString();
			}, {
				once: true
			}
		);
		tempImage.addEventListener(
			'error',
			() => {
				this.thumbnailEl.dispatchEvent(new UIEvent('error'));
			}, {
				once: true
			}
		);
		tempImage.src = thumbnailUrl.toString();
	}

	updateAvailability() {
		if (!this.isVisible) return;
		if (this.checkStarted()) {
			if (this.isThumbnailLoaded) {
				this.thumbnailWrapperEl.classList.remove('vjs-hidden');
				this.stubEl.classList.add('vjs-hidden');
			}
			this.inputEl.removeAttribute('disabled');
			this.trigger('MulticamCamera/enabled');
		} else {
			this.thumbnailWrapperEl.classList.add('vjs-hidden');
			this.stubEl.classList.remove('vjs-hidden');
			this.inputEl.setAttribute('disabled', '');
			this.trigger('MulticamCamera/disabled');
		}
	}

	checkSelected() {
		return this.inputEl.checked;
	}

	checkStarted() {
		return this.startAt < Date.now() / 1000;
	}

	checkFinished() {
		return Date.now() / 1000 >= (this.finishAt || this.options_.broadcast.finishAt);
	}

	checkDisabled() {
		return this.inputEl.disabled;
	}

	dispose() {
		clearTimeout(this.monitorTimeoutId);
	}

	changeData({ start_at, finish_at }) {
		this.startAt = start_at;
		this.finishAt = finish_at;
		this.trigger('MulticamCamera/changeData');
	}

	getBroadcastId() {
		return this.options_.broadcast.id;
	}

	getBroadcastName() {
		return this.options_.broadcast.name;
	}

	select() {
		this.el().click();
	}

	checkMain() {
		return this.options_.broadcast.isMain;
	}

	getThumbnailUrl(name, isSaveCurrentThumbnailName = true) {
		const url = this.options_.broadcast[`${name}ThumbnailUrl`];
		if (!url) return '';
		if (isSaveCurrentThumbnailName) {
			this.currentThumbnailName = name;
		}
		return url;
	}

	getRemainingTime(startAt) {
		const msPerHour = 3600000;
		const msPerMinute = 60000;
		const msPerSecond = 1000;

		const msRemaining = startAt * 1000 - Date.now();
		let hours = Math.floor(msRemaining / msPerHour);
		if (hours < 10) {
			hours = `0${hours}`;
		}
		const minutes = `0${Math.floor(msRemaining % msPerHour / msPerMinute)}`.slice(-2);
		const seconds = `0${Math.floor(msRemaining % msPerMinute / msPerSecond)}`.slice(-2);

		return `${hours}:${minutes}:${seconds}`;
	}

	getPlaybackTime(currentTime) {
		if (currentTime < 0 || currentTime >= this.player().duration()) {
			return 0;
		}
		return currentTime;
	}

	prepareAudioTracksOptions() {
		const uniqTracks = (tracks) => {
			let trackIds = new Set();
			let result = [];
			tracks.forEach(track => {
				if (trackIds.has(track.id)) {
					return;
				}
				result.push(track);
				trackIds.add(track.id);
			});
			return result;
		};

		const { initialOptions } = this.player().audiotracks();
		const tracks = this.options_.broadcast.audioTracks || [];
		const cameraTracksOptions = {
			tracks: tracks
		};
		if (tracks.length) {
			cameraTracksOptions.default = tracks[0].id;
		}
		const tracksOptions = videojs.mergeOptions(
			initialOptions,
			cameraTracksOptions
		);
		tracksOptions.tracks = uniqTracks(tracksOptions.tracks);
		return tracksOptions;
	}
});
